import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { CardComponent } from '../../../../shared/components/card/card.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  IconComponent,
  IconSize,
} from '../../../../shared/icons/icon.component';
import { InputWrapperComponent } from '../../../../shared/components/input/input-wrapper/input-wrapper.component';
import { DateRangeInputComponent } from '../../../../shared/components/input/date-range-input/date-range-input.component';
import { Store } from '@ngrx/store';
import { catchError, EMPTY, map, take } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { FilterDialogComponent } from '../../../../shared/components/filter-dialog/filter-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { withResetPageOnOtherFilterChange } from '../../../../shared/utils/filter-utils';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { DeviceActions } from '../../../devices/state/device.actions';
import { selectAllDevices } from '../../../devices/state/device.selectors';
import {
  selectActionLogFilter,
  selectActionLogTotalCount,
  selectFilteredActionLogWithCurrentPage,
  selectIsActionLogLoading,
} from '../../state/action-log.selectors';
import { ActionLogActions } from '../../state/action-log.actions';
import {
  ActionLogFilter,
  ActionLogSortBy,
  ActionLogSortDir,
} from '../../state/action-log.state';
import { ActionLogObjectType, ActionLogResult, DeviceType } from '@birco/rainmesh-api';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
import { TimePipe } from '../../../../shared/pipes/time.pipe';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { MatTooltip } from '@angular/material/tooltip';

type ActionLogSortOrder =
  | 'eventDateDesc'
  | 'eventDateAsc'
  | 'actionAsc'
  | 'actionDesc'
  | 'objectNameAsc'
  | 'objectNameDesc'
  | 'resultAsc'
  | 'resultDesc';

@Component({
  selector: 'app-page-action-log',
  imports: [
    PaginationComponent,
    CardComponent,
    AsyncPipe,
    TranslatePipe,
    ReactiveFormsModule,
    IconComponent,
    NgTemplateOutlet,
    InputWrapperComponent,
    DateRangeInputComponent,
    LocalizedDatePipe,
    TimePipe,
    TagComponent,
    MatTooltip,
    RouterLink,
  ],
  templateUrl: './page-action-log.component.html',
  styleUrl: './page-action-log.component.scss',
})
export class PageActionLogComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private store = inject(Store);
  private dialog = inject(MatDialog);

  private destroyRef = inject(DestroyRef);

  loading$ = this.store.select(selectIsActionLogLoading);
  totalCount$ = this.store.select(selectActionLogTotalCount);
  actionLog$ = this.store.select(selectFilteredActionLogWithCurrentPage);

  // Needed to tell pumps apart from other devices when resolving a DEVICE entry's detail route.
  private deviceTypeById = toSignal(
    this.store.select(selectAllDevices).pipe(
      map((devices) => new Map(devices.map((device) => [device.id, device.type]))),
    ),
    { initialValue: new Map<string, DeviceType>() },
  );

  @ViewChild('filterFields', { static: true })
  filterFieldsTemplate?: TemplateRef<unknown>;
  isFilterDialogOpen = false;

  filterForm: FormGroup;
  filterOptions = {
    result: [
      { label: 'default.all', value: null },
      ...Object.values(ActionLogResult).map((value) => ({
        value,
        label: LabelConverter.getActionLogResultLabel(value),
      })),
    ],
    sortOrder: [
      {
        label: 'actionLog.filter.sort.eventDateDesc',
        value: 'eventDateDesc' as const,
      },
      {
        label: 'actionLog.filter.sort.eventDateAsc',
        value: 'eventDateAsc' as const,
      },
      { label: 'actionLog.filter.sort.actionAsc', value: 'actionAsc' as const },
      {
        label: 'actionLog.filter.sort.actionDesc',
        value: 'actionDesc' as const,
      },
      {
        label: 'actionLog.filter.sort.objectNameAsc',
        value: 'objectNameAsc' as const,
      },
      {
        label: 'actionLog.filter.sort.objectNameDesc',
        value: 'objectNameDesc' as const,
      },
      { label: 'actionLog.filter.sort.resultAsc', value: 'resultAsc' as const },
      {
        label: 'actionLog.filter.sort.resultDesc',
        value: 'resultDesc' as const,
      },
    ],
  };

  constructor() {
    this.filterForm = this._fb.group({
      searchTerm: [null],
      result: [null],
      dateRange: [{ startDate: null, endDate: null }],
      sortOrder: ['eventDateDesc' as ActionLogSortOrder],
      page: [null],
      pageSize: [null],
    });
  }

  ngOnInit() {
    this.store.dispatch(DeviceActions.public.loadAllDevicesByFilters({ filter: {} }));

    this.filterForm.valueChanges
      .pipe(
        map((value) => this.mapFormValueToFilter(value)),
        withResetPageOnOtherFilterChange(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((newFilter) => {
        this.store.dispatch(
          ActionLogActions.public.updateActionLogFilter({ filter: newFilter }),
        );
      });

    this.store
      .select(selectActionLogFilter)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((_) => EMPTY),
      )
      .subscribe((filter) => {
        this.filterForm.patchValue(this.mapFilterToFormValue(filter));
      });
  }

  private mapFormValueToFilter(value: any): ActionLogFilter {
    const { dateRange, sortOrder, ...rest } = value;
    const { sortBy, sortDir } = this.mapSortOrderToFilterSort(sortOrder);
    return {
      ...rest,
      startDate: dateRange?.startDate ?? undefined,
      endDate: dateRange?.endDate ?? undefined,
      sortBy,
      sortDir,
    };
  }

  private mapFilterToFormValue(filter: ActionLogFilter) {
    const { startDate, endDate, sortBy, sortDir, ...rest } = filter;
    return {
      ...rest,
      dateRange: { startDate: startDate ?? null, endDate: endDate ?? null },
      sortOrder: this.mapFilterToSortOrder(
        sortBy ?? 'eventDate',
        sortDir ?? 'desc',
      ),
    };
  }

  private mapSortOrderToFilterSort(sortOrder?: ActionLogSortOrder): {
    sortBy: ActionLogSortBy;
    sortDir: ActionLogSortDir;
  } {
    switch (sortOrder) {
      case 'eventDateAsc':
        return { sortBy: 'eventDate', sortDir: 'asc' };
      case 'actionAsc':
        return { sortBy: 'action', sortDir: 'asc' };
      case 'actionDesc':
        return { sortBy: 'action', sortDir: 'desc' };
      case 'objectNameAsc':
        return { sortBy: 'objectName', sortDir: 'asc' };
      case 'objectNameDesc':
        return { sortBy: 'objectName', sortDir: 'desc' };
      case 'resultAsc':
        return { sortBy: 'result', sortDir: 'asc' };
      case 'resultDesc':
        return { sortBy: 'result', sortDir: 'desc' };
      case 'eventDateDesc':
      default:
        return { sortBy: 'eventDate', sortDir: 'desc' };
    }
  }

  private mapFilterToSortOrder(
    sortBy: ActionLogSortBy,
    sortDir: ActionLogSortDir,
  ): ActionLogSortOrder {
    if (sortBy === 'eventDate' && sortDir === 'asc') return 'eventDateAsc';
    if (sortBy === 'action' && sortDir === 'asc') return 'actionAsc';
    if (sortBy === 'action' && sortDir === 'desc') return 'actionDesc';
    if (sortBy === 'objectName' && sortDir === 'asc') return 'objectNameAsc';
    if (sortBy === 'objectName' && sortDir === 'desc') return 'objectNameDesc';
    if (sortBy === 'result' && sortDir === 'asc') return 'resultAsc';
    if (sortBy === 'result' && sortDir === 'desc') return 'resultDesc';
    return 'eventDateDesc';
  }

  onChangePage(p: PageEvent) {
    this.filterForm.controls['page'].patchValue(p.pageIndex);
  }

  openFilterDialog() {
    if (!this.filterFieldsTemplate || this.isFilterDialogOpen) return;
    this.isFilterDialogOpen = true;

    const dialogRef = this.dialog.open(FilterDialogComponent, {
      width: 'min(34rem, calc(100vw - 2rem))',
      maxWidth: '100vw',
      data: {
        titleKey: 'default.filter',
        contentTemplate: this.filterFieldsTemplate,
      },
    });

    this.filterForm.valueChanges
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => dialogRef.close());

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFilterDialogOpen = false;
      });
  }

  getObjectRoute(type: ActionLogObjectType, id?: string, name?: string): string | null {
    if (!id || !name) {
      return null;
    }

    switch (type) {
      case ActionLogObjectType.DEVICE: {
        const isPump = this.deviceTypeById().get(id) === DeviceType.PUMP;
        return isPump
          ? `/${RouteConstants.routePump}/${id}`
          : `/${RouteConstants.routeDevice}/${id}`;
      }
      case ActionLogObjectType.DEVICE_GROUP:
        return `/${RouteConstants.routeDeviceGroup}/${id}`;
      case ActionLogObjectType.CISTERN:
        return `/${RouteConstants.routeCistern}/${id}`;
      case ActionLogObjectType.RULE:
        return `/${RouteConstants.routeRule}/${id}`;
      case ActionLogObjectType.SYSTEM:
      default:
        return null;
    }
  }

  protected readonly LabelConverter = LabelConverter;
  protected readonly IconSize = IconSize;
}
